import React, { useState } from 'react';
import { motion } from 'motion/react';
import { 
  ShieldCheck, 
  FileText, 
  CheckCircle2,
  Lock,
  Phone,
  Mail,
  HelpCircle,
  Clock,
  Receipt,
  UserCheck,
  ChevronRight,
  ArrowUpRight,
  Download
} from 'lucide-react';

interface ClientPortalProps {
  onBack: () => void;
  onNavigateToServices?: () => void;
  onNavigateToWebsites?: () => void;
}

export const ClientPortal: React.FC<ClientPortalProps> = ({ 
  onBack,
  onNavigateToServices,
  onNavigateToWebsites 
}) => {
  const [copiedEmail, setCopiedEmail] = useState(false);
  const portalUrl = "https://invoice.zohosecure.com/portal/iamrandonbrown";

  const handleCopySupportEmail = () => {
    navigator.clipboard.writeText("help@randonbrown.com");
    setCopiedEmail(true);
    setTimeout(() => setCopiedEmail(false), 2000);
  };

  return (
    <div className="space-y-6 sm:space-y-8 max-w-5xl mx-auto px-3 sm:px-6 py-2 sm:py-4 w-full text-left">
      {/* Hero Welcome Header */}
      <div className="text-center max-w-2xl mx-auto space-y-2.5 px-2">
        <div className="inline-flex items-center gap-2 py-1 px-3.5 rounded-full bg-brand-500/10 text-brand-400 text-xs font-mono font-bold uppercase tracking-wider border border-brand-500/20 select-none">
          <FileText className="w-3.5 h-3.5" />
          Client Billing & Records
        </div>
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-display font-black text-white tracking-tight">
          Client Account & Billing Portal
        </h1>
        <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
          Access your client account to review active project estimates, track work orders, view itemized invoice statements, and download official receipts.
        </p>
      </div>

      {/* Main Launchpad Hero Card */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-neutral-900/90 via-neutral-950 to-neutral-950 border border-brand-500/30 p-6 sm:p-8 md:p-10 shadow-2xl space-y-6">
        {/* Ambient Glow */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-brand-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-amber-500/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pb-6 border-b border-neutral-900">
          <div className="space-y-2 max-w-xl">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-emerald-400">
                Active Client Portal
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-display font-bold text-white">
              Open Your Zoho Client Invoicing Portal
            </h2>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              The client ledger is hosted securely on Zoho Invoice. Click below to view your personalized statement, review quote estimates, and download account documentation in a dedicated tab.
            </p>
          </div>

          <a
            href={portalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full md:w-auto px-8 py-4 rounded-2xl bg-brand-500 hover:bg-brand-400 text-neutral-950 text-sm font-bold font-mono uppercase tracking-wider flex items-center justify-center gap-2.5 shadow-xl shadow-brand-500/20 active:scale-95 transition-all duration-200 cursor-pointer shrink-0"
          >
            <span>Open Client Portal</span>
            <ArrowUpRight className="w-4 h-4 stroke-[3]" />
          </a>
        </div>

        {/* Feature Highlights Grid */}
        <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3.5">
          <div className="p-4 rounded-2xl bg-black/40 border border-neutral-800/80 space-y-2">
            <div className="p-2.5 rounded-xl bg-brand-500/10 text-brand-400 w-fit">
              <FileText className="w-4 h-4" />
            </div>
            <h3 className="text-xs font-bold text-white">Itemized Invoices</h3>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Review current balances, due dates, and itemized labor/hardware breakdowns.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-black/40 border border-neutral-800/80 space-y-2">
            <div className="p-2.5 rounded-xl bg-emerald-500/10 text-emerald-400 w-fit">
              <CheckCircle2 className="w-4 h-4" />
            </div>
            <h3 className="text-xs font-bold text-white">Accept Estimates</h3>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Digitally sign and approve project scopes or repair proposals in real-time.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-black/40 border border-neutral-800/80 space-y-2">
            <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-400 w-fit">
              <Receipt className="w-4 h-4" />
            </div>
            <h3 className="text-xs font-bold text-white">Download Receipts</h3>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Export and print official PDF payment receipts for tax and accounting records.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-black/40 border border-neutral-800/80 space-y-2">
            <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400 w-fit">
              <Clock className="w-4 h-4" />
            </div>
            <h3 className="text-xs font-bold text-white">Service History</h3>
            <p className="text-[11px] text-slate-400 leading-relaxed">
              Access the complete ledger of all historical technical and web design projects.
            </p>
          </div>
        </div>
      </div>

      {/* Step-by-Step Client Guide */}
      <div className="p-6 sm:p-8 rounded-3xl bg-neutral-950 border border-neutral-900 space-y-6 shadow-lg">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-brand-500/10 text-brand-400 border border-brand-500/20">
            <UserCheck className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-white font-display">
              How to Access Your Account
            </h2>
            <p className="text-xs text-brand-400 font-mono">Simple 3-Step Client Protocol</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <div className="p-4 rounded-2xl bg-neutral-900/60 border border-neutral-800/80 space-y-2">
            <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md bg-brand-500/20 text-brand-400">
              STEP 01
            </span>
            <h3 className="font-bold text-white text-sm">Enter Your Client Email</h3>
            <p className="text-slate-400 leading-relaxed text-xs">
              When the Zoho gateway opens, sign in using the email address you provided for your service work or invoice.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-neutral-900/60 border border-neutral-800/80 space-y-2">
            <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md bg-brand-500/20 text-brand-400">
              STEP 02
            </span>
            <h3 className="font-bold text-white text-sm">Review Active Statements</h3>
            <p className="text-slate-400 leading-relaxed text-xs">
              View your pending quotes, approved project milestones, and live diagnostic work orders.
            </p>
          </div>

          <div className="p-4 rounded-2xl bg-neutral-900/60 border border-neutral-800/80 space-y-2">
            <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-md bg-brand-500/20 text-brand-400">
              STEP 03
            </span>
            <h3 className="font-bold text-white text-sm">Download PDF Documentation</h3>
            <p className="text-slate-400 leading-relaxed text-xs">
              Instantly save PDF estimates or invoices directly to your computer or mobile device.
            </p>
          </div>
        </div>
      </div>

      {/* Direct In-App Contact & Assistance Card */}
      <div className="p-5 sm:p-6 rounded-3xl bg-neutral-950/80 border border-brand-500/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs">
        <div className="flex items-center gap-3.5">
          <div className="p-3 rounded-2xl bg-brand-500/10 text-brand-400 border border-brand-500/20 shrink-0">
            <HelpCircle className="w-5 h-5" />
          </div>
          <div className="space-y-0.5">
            <h3 className="text-sm font-bold text-white">Need a direct copy emailed to you?</h3>
            <p className="text-xs text-slate-400">
              If you have any questions regarding your invoice or need a copy resent, we are here to assist.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2.5 w-full sm:w-auto shrink-0">
          <button
            onClick={handleCopySupportEmail}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold transition-colors cursor-pointer"
          >
            <Mail className="w-3.5 h-3.5 text-brand-400" />
            <span>{copiedEmail ? 'Copied Email!' : 'help@randonbrown.com'}</span>
          </button>

          <a
            href="tel:3149443074"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-slate-300 hover:text-white border border-neutral-800 text-xs font-semibold transition-colors"
          >
            <Phone className="w-3.5 h-3.5 text-emerald-400" />
            <span>(314) 944-3074</span>
          </a>
        </div>
      </div>
    </div>
  );
};
